import { randomDelay } from "@/lib/utils/sleep";
import { publicProposalPartnerUrl } from "@/lib/public-url";
import { generateShareCode } from "@/lib/utils/share-code";
import type { BrUf } from "@/lib/format/uf";
import type {
  PropertyCategory,
  PropertyFeature,
  PropertyOperation,
  PropertyStatus,
  PropertyType,
} from "../schemas/property";

export interface PropertyAddress {
  zipcode: string;
  street?: string;
  number?: string;
  complement?: string;
  neighborhood: string;
  city: string;
  state: BrUf;
  hideExactAddress?: boolean;
  /** Coordenadas do imóvel (pino confirmado no mapa). Persistidas no crm-api. */
  latitude?: number;
  longitude?: number;
}

export interface PropertyImage {
  id: string;
  url: string;
  thumbnailUrl?: string;
  isCover: boolean;
  order: number;
  caption?: string;
  /** Arquivo ainda não enviado (cadastro): preview local até o upload no submit. */
  file?: Blob;
}

/**
 * Estado do vídeo no ciclo de vida do Vimeo (hub#636):
 * `uploading` = bytes subindo via tus; `processing` = Vimeo transcodificando;
 * `ready` = pronto p/ tocar (tem `embedUrl`); `error` = falhou.
 */
export type PropertyVideoStatus = "uploading" | "processing" | "ready" | "error";

/** Vídeo curto da galeria do imóvel (máx. 3), hospedado no Vimeo. Espelha o crm-api. hub#636. */
export interface PropertyVideo {
  id: string;
  /** Id do vídeo no Vimeo. Ausente enquanto o arquivo é só um staging local. */
  vimeoId?: string;
  /** Estado do processamento no Vimeo. */
  status: PropertyVideoStatus;
  order: number;
  caption?: string;
  /** URL do Vimeo Player (player.vimeo.com, já com o hash de unlisted). Só quando `ready`. */
  embedUrl?: string;
  /** Miniatura gerada pelo Vimeo. */
  thumbnailUrl?: string;
  /** Duração em segundos (metadado de exibição). */
  durationSeconds?: number;
  /** Arquivo ainda não enviado (cadastro): staging local até o upload tus no submit. */
  file?: Blob;
  /** Progresso do upload tus (0–100), só no staging local antes de subir. */
  progress?: number;
}

/** Máximo de vídeos curtos por imóvel (regra de produto, hub#636). */
export const MAX_PROPERTY_VIDEOS = 3;
/** Teto de tamanho por vídeo (50 MB), alinhado ao cap do crm-api. */
export const MAX_PROPERTY_VIDEO_BYTES = 50 * 1024 * 1024;
/** Duração máxima recomendada por vídeo (~1min40). */
export const MAX_PROPERTY_VIDEO_SECONDS = 100;

export type IntegrationChannel =
  | "olx"
  | "zap"
  | "viva_real"
  | "imobgo_showcase"
  | "instagram"
  | "facebook";

export type PublicationStatus = "inactive" | "active" | "paused" | "error" | "pending";

export interface PropertyPublication {
  channel: IntegrationChannel;
  status: PublicationStatus;
  publishedAt?: string;
  externalUrl?: string;
  views?: number;
  leads?: number;
  errorMessage?: string;
}

export type ProposalHistoryStatus =
  | "draft"
  | "sent"
  | "negotiating"
  | "accepted"
  | "rejected"
  | "expired";

export interface PropertyProposalRef {
  id: string;
  number: string;
  // Dinheiro como `number` (reais) — money-as-number.
  amount: number;
  status: ProposalHistoryStatus;
  proposerName: string;
  receivedAt: string;
}

export type InspectionKind = "entry" | "exit" | "periodic";
export type InspectionStatus = "scheduled" | "in_progress" | "completed" | "canceled";

export interface PropertyInspectionRef {
  id: string;
  kind: InspectionKind;
  status: InspectionStatus;
  scheduledFor?: string;
  completedAt?: string;
  reportPdfUrl?: string;
}

export interface PropertyShareLink {
  id: string;
  token: string;
  showAddress: boolean;
  /** Se o link mostra a galeria de vídeos curtos ao cliente (hub#617). Default: não. */
  includeVideos: boolean;
  createdAt: string;
  expiresAt?: string;
  maxViews?: number;
  viewCount: number;
  note?: string;
  revoked: boolean;
  /** Link sem a identidade do corretor dono — separa as abas da tela. hub#765. */
  hideBrokerIdentity?: boolean;
  /**
   * Nome de quem pediu o link (só na modalidade sem identidade). E-mail e telefone não
   * vêm nesta visão embutida no imóvel — ficam no endpoint de tokens, que só o dono lê.
   */
  requesterName?: string | null;
}

/** Métricas do link público do imóvel — espelha o DTO do crm-api. hub#577. */
export interface ShareVisitRecent {
  occurredAt: string;
  deviceKind: string;
  deviceName: string;
  browser: string;
  os: string;
}

export interface ShareVisitTimelinePoint {
  date: string;
  clicks: number;
}

export interface ShareAnalytics {
  shareCode: string;
  publicUrl: string;
  totalClicks: number;
  uniqueVisitors: number;
  firstClickAt: string | null;
  lastClickAt: string | null;
  timeline: ShareVisitTimelinePoint[];
  byDeviceKind: Record<string, number>;
  byBrowser: Record<string, number>;
  byOs: Record<string, number>;
  recent: ShareVisitRecent[];
}

/** Página de acessos (cursor) do link público — `GET /share-analytics/visits`. hub#577. */
export interface ShareVisitsPage {
  items: ShareVisitRecent[];
  nextCursor: string | null;
  hasMore: boolean;
  count: number;
}

export type PropertyTransactionKind = "sale" | "rent";
export type PropertyTransactionStatus = "active" | "closed" | "canceled";

/** Histórico de vendas/locações realizadas para este imóvel (snapshot de Deals + Contracts já fechados ou ativos). */
export interface PropertyTransaction {
  id: string;
  kind: PropertyTransactionKind;
  status: PropertyTransactionStatus;
  // Dinheiro como `number` (reais) — money-as-number.
  amount: number;
  /** Cliente (comprador/locatário). */
  partyName: string;
  /** Quem fez o negócio (pode ter sido outro corretor numa parceria). */
  brokerName: string;
  /** Para venda: data da escritura. Para locação: data de início. */
  startDate: string;
  /** Para locação: data fim do contrato. Para venda: undefined. */
  endDate?: string;
  dealId?: string;
  dealCode?: string;
  contractId?: string;
  contractCode?: string;
  note?: string;
  // ── Desfecho do encerramento (hub#759) ──
  /** Motivo estruturado; ausente = ainda ativo, ou encerrado antes desta feature. */
  closureKind?: ContractClosureKind;
  closureRequestedBy?: ClosureRequestedBy;
  keysReturnedOn?: string;
  closureNotes?: string;
  penaltyAmount?: number;
  /** Vistoria de saída com laudo pronto, quando existe. */
  exitInspectionId?: string;
  // ── Consulta ao proprietário (hub#760) ──
  returnCheckId?: string;
  returnCheckStatus?: PropertyReturnStatus;
  returnAnswer?: PropertyReturnAnswer;
  returnAnswerSource?: PropertyReturnAnswerSource;
  returnAnsweredAt?: string;
  returnExpectedAvailableOn?: string;
}

/** Motivo do encerramento do contrato (hub#759) — mesmo contrato do crm-api. */
export type ContractClosureKind =
  | "end_of_term"
  | "early_termination"
  | "breach"
  | "sale_unwound"
  | "other";

/** Quem pediu o encerramento — só em distrato/rescisão. */
export type ClosureRequestedBy = "tenant" | "owner" | "mutual" | "broker";

/** O que o proprietário decidiu sobre o imóvel depois do encerramento (hub#760). */
export type PropertyReturnAnswer = "relist" | "repairs_first" | "pause" | "withdraw";

export type PropertyReturnStatus = "pending" | "answered" | "expired" | "canceled";

/** Quem registrou a resposta: o dono na página pública, ou o corretor por fora. */
export type PropertyReturnAnswerSource = "owner_page" | "broker";

/**
 * Consulta ao proprietário aberta pelo encerramento de um contrato
 * (`GET /v1/properties/{id}/return-checks`). hub#760.
 */
export interface PropertyReturnCheck {
  id: string;
  propertyId: string;
  contractId?: string;
  status: PropertyReturnStatus;
  closureKind: ContractClosureKind;
  closureRequestedBy?: ClosureRequestedBy;
  sentAt: string;
  expiresAt: string;
  answer?: PropertyReturnAnswer;
  answerSource?: PropertyReturnAnswerSource;
  answeredAt?: string;
  message?: string;
  expectedAvailableOn?: string;
  reminderCount: number;
  /** E-mail para o qual o link foi enviado; ausente = só link copiável. */
  sentTo?: string;
  /** Link público para copiar/compartilhar com o dono. */
  url: string;
}

export type PartnershipStatus = "invited" | "accepted" | "declined" | "revoked" | "expired";
export type PartnerIdentifierKind = "email" | "creci" | "imb" | "phone";

export interface PropertyPartnership {
  id: string;
  identifierKind: PartnerIdentifierKind;
  identifier: string;
  /** Nome amigável (resolvido após aceite). */
  partnerName?: string;
  ownerPercent: number;
  partnerPercent: number;
  status: PartnershipStatus;
  note?: string;
  invitedAt: string;
  respondedAt?: string;
  revokedAt?: string;
  /** Quando o parceiro aceitou, há uma proposta ativa? */
  hasActiveProposal?: boolean;
}

/**
 * Convite de co-corretagem RECEBIDO pelo corretor logado (caixa do convidado).
 * A propriedade é de outro corretor — só o resumo público viaja, sem expor o book.
 */
export interface ReceivedPartnership {
  id: string;
  propertyId: string;
  propertyTitle: string;
  propertyCode: string;
  ownerBrokerName: string;
  ownerPercent: number;
  partnerPercent: number;
  status: PartnershipStatus;
  note?: string | null;
  invitedAt: string;
}

/** Preview de corretor da plataforma achado por CRECI/IMB (cenário A). Só dados públicos mínimos. */
export interface BrokerLookupResult {
  found: boolean;
  brokerId?: string;
  publicCode?: string;
  name?: string;
}

/** Resultado da busca global de um imóvel do próprio corretor pelo código público IMV- (#129). */
export interface PropertyLookupResult {
  found: boolean;
  propertyId?: string;
  publicCode?: string;
  title?: string;
  status?: PropertyStatus;
}

// ---------- Autorização de acesso ao imóvel ----------
// Caso de uso: imóveis dentro de condomínio ou áreas restritas precisam que
// o proprietário avise a portaria com antecedência. O corretor solicita,
// gera link único e envia ao proprietário (via WhatsApp). Proprietário
// confirma na portaria, depois acessa o link e marca como autorizado.

export type AccessAuthorizationStatus =
  | "requested" // criada pelo corretor, link enviado ao proprietário, aguardando
  | "authorized" // proprietário confirmou que liberou na portaria
  | "denied" // proprietário negou
  | "expired" // passou da data sem confirmação
  | "canceled"; // corretor cancelou

export interface AuthorizationVisitor {
  id: string;
  nome: string;
  documento?: string;
  telefone?: string;
}

export interface PropertyAccessAuthorization {
  id: string;
  /** Token opaco usado na URL pública. */
  token: string;
  /** Data + hora prevista para o acesso (ISO). */
  scheduledAt: string;
  /** Duração estimada da visita em minutos (informativo). */
  durationMinutes?: number;
  visitors: AuthorizationVisitor[];
  /** WhatsApp do proprietário (com DDI/DDD, só dígitos). */
  ownerPhone: string;
  status: AccessAuthorizationStatus;
  note?: string;
  createdAt: string;
  /** Quem criou (corretor) — nome do snapshot. */
  requestedByName: string;
  /** Quando o proprietário respondeu via link público. */
  ownerRespondedAt?: string;
  ownerResponseNote?: string;
}

/** Papel do usuário atual em relação ao imóvel (mock derivado do JWT no backend). */
export type ViewerRole = "owner" | "partner";

export interface Property {
  id: string;
  internalCode: string;
  /**
   * Código público canônico global `IMV-NNNNNN` (#122). Imutável, único entre
   * todos os corretores; é o código exibido (badges) e aceito na busca global.
   */
  publicCode?: string;
  title: string;
  type: PropertyType;
  category: PropertyCategory;
  operation: PropertyOperation;
  status: PropertyStatus;
  salePrice?: number;
  rentPrice?: number;
  condoFee?: number;
  iptuYearly?: number;
  bedrooms?: number;
  suites?: number;
  bathrooms?: number;
  parkingSpots?: number;
  builtArea?: number;
  totalArea?: number;
  floor?: number;
  /** Medidas do TERRENO (só para type=land), em metros: frente=largura, profundidade=lado, fundo (se diverge da frente). */
  landFront?: number | null;
  landDepth?: number | null;
  landRear?: number | null;
  features: PropertyFeature[];
  description?: string;
  /**
   * Proprietário (Person) do imóvel. SEMPRE null/ausente quando o imóvel foi
   * recebido via co-broking — o backend mascara; o parceiro nunca vê o dono.
   */
  ownerId?: string;
  ownerName?: string;
  address: PropertyAddress;
  images: PropertyImage[];
  /** Galeria de vídeos curtos (máx. 3). hub#617. */
  videos?: PropertyVideo[];
  videoUrl?: string;
  virtualTourUrl?: string;
  /** Código opaco curto e globalmente único — usado em /p/{shareCode}. */
  shareCode: string;
  /** Slug SEO derivado do título; informativo. NÃO é único entre corretores. */
  publicSlug: string;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  publications?: PropertyPublication[];
  proposals?: PropertyProposalRef[];
  inspections?: PropertyInspectionRef[];
  shareLinks?: PropertyShareLink[];
  partnerships?: PropertyPartnership[];
  /** Histórico de vendas/locações deste imóvel. */
  transactions?: PropertyTransaction[];
  /** Autorizações de acesso solicitadas para este imóvel (condomínio, portaria). */
  accessAuthorizations?: PropertyAccessAuthorization[];
  /** Imóvel em condomínio? Quando true, `condominiumName` é exibido (inclusive nas páginas públicas/co-broking). */
  isCondominium?: boolean;
  /** Nome do condomínio (quando `isCondominium`). */
  condominiumName?: string | null;
  /** Se o corretor administra o imóvel (gerência de locação, repasse ao proprietário, etc.). */
  isManaged?: boolean;
  /** Taxa de administração (%) cobrada pelo corretor. */
  managementFeePercent?: number;
  /**
   * Comissão (%) sobre o valor de venda. Default da plataforma é 6%.
   * Base do split de co-broking — partner recebe % desse total.
   */
  saleCommissionPercent?: number;
  /**
   * Comissão de locação. Padrão BR: 1 aluguel completo (100%) como taxa
   * de intermediação. Pode variar (50%, 80%, etc.) conforme negociação.
   * Base do split de co-broking em locação.
   */
  rentCommissionPercent?: number;
  /**
   * Papel do usuário atual neste imóvel — derivado do JWT no backend real.
   * "owner" = corretor titular (criou o imóvel); "partner" = parceiro de co-broking.
   * No mock, default "owner" para todos. Parceiros NÃO têm permissão de gerenciar
   * autorização de acesso ao imóvel (regra do produto).
   */
  viewerRole?: ViewerRole;
  /**
   * Resumo de co-corretagem quando o imóvel foi RECEBIDO (o corretor atual é o
   * parceiro com share ativo): nome do corretor dono + % do parceiro no split.
   * Null/ausente quando o corretor é o dono.
   */
  coBroking?: { fromName: string; splitPercent: number };
  /**
   * Foto dedicada de preview do link público (og:image no WhatsApp/redes). NÃO
   * entra na galeria de fotos do imóvel; definida pelo endpoint próprio
   * `PUT /properties/{id}/preview-image`. hub#577.
   */
  previewImageUrl?: string | null;
  /**
   * Periodicidade (em dias) do check-in de disponibilidade com o proprietário.
   * null/ausente = desligado. Valores fixos: 7, 15, 30, 90.
   */
  availabilityCheckIntervalDays?: number | null;
  /** Quando o próximo check-in será disparado ao proprietário (agendado). */
  nextAvailabilityCheckAt?: string | null;
  /** Quando o último check-in foi enviado ao proprietário. */
  lastAvailabilityCheckAt?: string | null;
}

/** Status de um check-in de disponibilidade (molde AccessAuthorizationStatus). */
export type AvailabilityCheckStatus = "pending" | "responded" | "expired";

/** Resposta do proprietário ao check-in (igual ao contrato do backend). */
export type AvailabilityCheckAnswer = "available" | "closed_deal" | "paused";

/**
 * Item do histórico de check-ins de disponibilidade de um imóvel
 * (`GET /v1/properties/{id}/availability-checks`).
 */
export interface PropertyAvailabilityCheck {
  id: string;
  sentAt: string;
  status: AvailabilityCheckStatus;
  answer?: AvailabilityCheckAnswer | null;
  ownerNote?: string | null;
  respondedAt?: string | null;
}

/** Canais pelos quais a carta de boas-vindas foi entregue ao proprietário. */
export type OwnerWelcomeChannels = "email" | "whatsapp" | "both";

/**
 * Estado da carta de boas-vindas ao proprietário
 * (`GET/POST /v1/properties/{id}/owner-welcome`). hub#707.
 */
export interface OwnerWelcome {
  id: string;
  propertyId: string;
  ownerId: string;
  ownerName?: string | null;
  channels: OwnerWelcomeChannels;
  personalMessage?: string | null;
  sentAt: string;
  expiresAt: string;
  firstViewedAt?: string | null;
  lastViewedAt?: string | null;
  viewCount: number;
  /** De onde veio o primeiro acesso: email | whatsapp | direct. */
  firstViewChannel?: string | null;
  publicUrl: string;
  /** wa.me pronto; null quando o dono não tem celular cadastrado. */
  whatsappUrl?: string | null;
  whatsappText: string;
  /** Já havia um envio vivo: nada foi reenviado nesta chamada. */
  alreadySent: boolean;
}

/** Corpo do envio das boas-vindas. `resend` gera um link novo de propósito. */
export interface SendOwnerWelcomeInput {
  channels: OwnerWelcomeChannels;
  personalMessage?: string;
  resend?: boolean;
}

// ── Gerador de Placa (hub#709) ───────────────────────────────────────────

/**
 * Medidas do menu. As de gráfica (`cm_*`) saem 1:1 com sangria de 5 mm, marcas de
 * corte e fechamento CMYK; `a4`/`a3` saem em RGB, sem sangria, para a impressora
 * do escritório.
 */
export type PropertySignSize = "cm_30x20" | "cm_60x40" | "cm_70x50" | "cm_90x60" | "a4" | "a3";

/** Escolha explícita do corretor — toda medida existe nas duas. */
export type PropertySignOrientation = "horizontal" | "vertical";

/** A cor de destaque é fixa na cor da marca no MVP; o tema muda o arranjo. */
export type PropertySignTheme = "branco" | "faixa" | "painel" | "moldura";

export type PropertySignStatus = "queued" | "processing" | "ready" | "failed";

/** `property` = placa do imóvel; `broker` = institucional (Fatia 2). */
export type PropertySignKind = "property" | "broker";

/** Pedido de placa (`POST/GET /v1/properties/{id}/sign`). hub#709. */
export interface PropertySign {
  id: string;
  propertyId?: string | null;
  kind: PropertySignKind;
  size: PropertySignSize;
  orientation: PropertySignOrientation;
  theme: PropertySignTheme;
  monochrome: boolean;
  showPrice: boolean;
  status: PropertySignStatus;
  generatedAt?: string | null;
  /** Motivo legível quando `failed` — a UI diz algo melhor que "tente de novo". */
  failureReason?: string | null;
  createdAt: string;
  /** URL assinada do PDF. Só quando `ready`. */
  pdfUrl?: string | null;
  /** PNG da 1ª página: a conferência antes de baixar (mesmo motor do PDF). */
  previewUrl?: string | null;
  urlsExpireAt?: string | null;
  /**
   * Token do link IMPRESSO nesta placa (spec §9.0). O QR carrega este valor, não o
   * shareCode do imóvel — é o que permite desativar UMA tiragem.
   */
  linkToken?: string | null;
  /** Link vivo? Desativado, o QR passa a levar ao cartão do corretor. */
  linkActive?: boolean;
}

/** Corpo do pedido. Nada é digitado: tudo vem do cadastro e do perfil. */
export interface CreatePropertySignInput {
  size: PropertySignSize;
  orientation: PropertySignOrientation;
  theme?: PropertySignTheme;
  monochrome?: boolean;
  /** Default `false` de propósito: placa impressa não atualiza quando o preço muda. */
  showPrice?: boolean;
  /**
   * Chamada da placa INSTITUCIONAL. Catálogo fechado por MEDIDA, não por curadoria:
   * `ALUGA-SE` sai com letra de ~46 mm na 60×40, enquanto qualquer frase acima de ~11
   * caracteres desaba para o piso do ajuste de corpo (~25 mm) — metade da altura é metade
   * da distância de leitura. Ignorada na placa de imóvel, onde a chamada vem da operação
   * do cadastro (escolher ali permitiria imprimir VENDE-SE num imóvel só de aluguel).
   */
  headline?: PropertySignHeadline;
}

/** Chamadas disponíveis na placa institucional (hub#709). `padrao` é a frase de 2 linhas. */
export type PropertySignHeadline =
  | "padrao"
  | "aluga_se"
  | "vende_se"
  | "consulte_me"
  | "fale_comigo";

/** Ordem de exibição no seletor — da maior letra para a menor. */
export const SIGN_HEADLINES: PropertySignHeadline[] = [
  "padrao",
  "aluga_se",
  "vende_se",
  "consulte_me",
  "fale_comigo",
];

/**
 * Imóvel recebido via co-broking (o corretor atual é o parceiro, não o dono).
 * `coBroking` é o sinal do backend real; `viewerRole` é o sinal legado do mock.
 */
export function isSharedProperty(p: Property): boolean {
  return !!p.coBroking || p.viewerRole === "partner";
}

/**
 * Shape ENXUTO devolvido pelo endpoint público (`GET /public/properties/{code}`),
 * consumido pela vitrine anônima. NÃO contém dados sensíveis ou internos do
 * imóvel — sem proprietário (ownerId/ownerName), comissões (sale/rent
 * CommissionPercent), parcerias/co-broking, share links, propostas, vistorias,
 * transações, autorizações de acesso, administração nem viewerRole.
 *
 * O backend já anula esses campos no DTO público; este type é hardening de
 * defesa em profundidade no front (auditoria P2-public-property-fe-type) para
 * impedir que a UI pública passe a depender — ou exponha — campos privados.
 */
export interface PublicProperty {
  id: string;
  internalCode: string;
  publicCode?: string;
  title: string;
  type: PropertyType;
  category: PropertyCategory;
  operation: PropertyOperation;
  status: PropertyStatus;
  salePrice?: number;
  rentPrice?: number;
  condoFee?: number;
  iptuYearly?: number;
  bedrooms?: number;
  suites?: number;
  bathrooms?: number;
  parkingSpots?: number;
  builtArea?: number;
  totalArea?: number;
  floor?: number;
  /** Medidas do TERRENO (só para type=land), em metros: frente=largura, profundidade=lado, fundo (se diverge da frente). */
  landFront?: number | null;
  landDepth?: number | null;
  landRear?: number | null;
  features: PropertyFeature[];
  description?: string;
  address: PropertyAddress;
  images: PropertyImage[];
  /** Vídeos curtos — só vêm quando o link público concede IncludeVideos. hub#617. */
  videos?: PropertyVideo[];
  videoUrl?: string;
  virtualTourUrl?: string;
  shareCode: string;
  publicSlug: string;
  publishedAt?: string;
}

/**
 * Projeta um `Property` para o `PublicProperty` enxuto, copiando SÓ os campos
 * seguros e descartando explicitamente owner/comissão/parcerias/share links/etc.
 * Mesmo que o backend (por bug ou regressão) devolva campos privados, eles não
 * atravessam esta projeção.
 */
export function toPublicProperty(p: Property): PublicProperty {
  return {
    id: p.id,
    internalCode: p.internalCode,
    publicCode: p.publicCode,
    title: p.title,
    type: p.type,
    category: p.category,
    operation: p.operation,
    status: p.status,
    salePrice: p.salePrice,
    rentPrice: p.rentPrice,
    condoFee: p.condoFee,
    iptuYearly: p.iptuYearly,
    bedrooms: p.bedrooms,
    suites: p.suites,
    bathrooms: p.bathrooms,
    parkingSpots: p.parkingSpots,
    builtArea: p.builtArea,
    totalArea: p.totalArea,
    floor: p.floor,
    landFront: p.landFront,
    landDepth: p.landDepth,
    landRear: p.landRear,
    features: p.features,
    description: p.description,
    address: p.address,
    images: p.images,
    videos: p.videos,
    videoUrl: p.videoUrl,
    virtualTourUrl: p.virtualTourUrl,
    shareCode: p.shareCode,
    publicSlug: p.publicSlug,
    publishedAt: p.publishedAt,
  };
}

const now = Date.now();
const iso = (daysAgo: number) => new Date(now - daysAgo * 86_400_000).toISOString();

/**
 * Pool de imagens Pixabay (licença Pixabay, uso livre sem atribuição).
 * Cada conjunto é um imóvel diferente — 3 fotos cada (capa + 2 da galeria).
 * Fonte: https://pixabay.com/pt/images/search/casas/
 */
const PIXABAY_HOUSES: { cover: string; gallery: string[] }[] = [
  // Casa térrea com piscina/quintal
  {
    cover: "https://cdn.pixabay.com/photo/2016/11/29/03/53/house-1867187_1280.jpg",
    gallery: [
      "https://cdn.pixabay.com/photo/2017/03/28/12/11/chairs-2181994_1280.jpg",
      "https://cdn.pixabay.com/photo/2014/07/10/17/18/large-home-389271_1280.jpg",
    ],
  },
  // Apartamento alto-padrão / vista mar
  {
    cover: "https://cdn.pixabay.com/photo/2016/11/18/17/20/living-room-1835923_1280.jpg",
    gallery: [
      "https://cdn.pixabay.com/photo/2017/08/02/01/01/living-room-2569325_1280.jpg",
      "https://cdn.pixabay.com/photo/2016/11/18/13/06/balcony-1834910_1280.jpg",
    ],
  },
  // Cobertura duplex luxo
  {
    cover: "https://cdn.pixabay.com/photo/2017/08/07/14/02/people-2602736_1280.jpg",
    gallery: [
      "https://cdn.pixabay.com/photo/2017/07/09/03/19/home-2486092_1280.jpg",
      "https://cdn.pixabay.com/photo/2016/11/21/16/35/luxury-1846137_1280.jpg",
    ],
  },
  // Apto pequeno mobiliado
  {
    cover: "https://cdn.pixabay.com/photo/2016/11/30/08/48/living-room-1872195_1280.jpg",
    gallery: [
      "https://cdn.pixabay.com/photo/2017/03/22/17/39/kitchen-2165756_1280.jpg",
      "https://cdn.pixabay.com/photo/2018/02/15/12/55/bedroom-3155602_1280.jpg",
    ],
  },
  // Terreno
  {
    cover: "https://cdn.pixabay.com/photo/2017/03/02/19/40/desert-2113497_1280.jpg",
    gallery: [
      "https://cdn.pixabay.com/photo/2016/11/14/04/45/grass-1822554_1280.jpg",
    ],
  },
  // Sala comercial alto-padrão
  {
    cover: "https://cdn.pixabay.com/photo/2016/11/19/13/06/balcony-1838418_1280.jpg",
    gallery: [
      "https://cdn.pixabay.com/photo/2017/03/19/20/19/architecture-2158963_1280.jpg",
    ],
  },
];

function pix(propIndex: number, kind: "cover" | "gallery", galleryIdx = 0): string {
  const entry = PIXABAY_HOUSES[propIndex % PIXABAY_HOUSES.length]!;
  if (kind === "cover") return entry.cover;
  return entry.gallery[galleryIdx % entry.gallery.length] ?? entry.cover;
}


let store: Property[] = [
  {
    id: "prop_01",
    internalCode: "IMV-0125",
    publicCode: "IMV-000125",
    title: "Casa 3qts com piscina · Sim, Feira de Santana",
    type: "house",
    category: "residential",
    operation: "sale",
    status: "active",
    salePrice: 650000.00,
    iptuYearly: 1800.00,
    bedrooms: 3,
    suites: 1,
    bathrooms: 3,
    parkingSpots: 2,
    builtArea: 180,
    totalArea: 360,
    features: ["pool", "barbecue", "backyard", "accepts_financing", "covered_garage"],
    description:
      "Casa térrea recém-reformada com 3 quartos (1 suíte), sala ampla, cozinha planejada, área gourmet com churrasqueira e piscina aquecida. Lote de esquina.",
    ownerId: "person_04",
    ownerName: "Ana Souza",
    address: {
      zipcode: "44086000",
      street: "Rua das Mangueiras",
      number: "123",
      neighborhood: "Sim",
      city: "Feira de Santana",
      state: "BA",
    },
    images: [
      { id: "img_01_01", url: pix(0, "cover"), isCover: true, order: 0 },
      { id: "img_01_02", url: pix(0, "gallery", 0), isCover: false, order: 1 },
      { id: "img_01_03", url: pix(0, "gallery", 1), isCover: false, order: 2 },
    ],
    videos: [
      {
        id: "vid_01_01",
        // Vídeo público de amostra do Vimeo — só no mock (representa um vídeo já "ready").
        vimeoId: "76979871",
        status: "ready",
        order: 0,
        durationSeconds: 15,
        caption: "Tour pela sala e varanda",
        embedUrl: "https://player.vimeo.com/video/76979871",
      },
    ],
    shareCode: "PRP1AABC01",
    publicSlug: "casa-3qts-piscina-sim-feira-de-santana-imv-0125",
    createdAt: iso(40),
    updatedAt: iso(2),
    publishedAt: iso(38),
    publications: [
      { channel: "olx", status: "active", publishedAt: iso(35), externalUrl: "https://www.olx.com.br/anuncio/exemplo-imv-0125", views: 412, leads: 7 },
      { channel: "zap", status: "active", publishedAt: iso(30), externalUrl: "https://www.zapimoveis.com.br/anuncio/exemplo", views: 286, leads: 4 },
      { channel: "viva_real", status: "paused", publishedAt: iso(28), views: 110, leads: 1 },
      { channel: "imobgo_showcase", status: "active", publishedAt: iso(38), views: 198, leads: 5 },
      { channel: "instagram", status: "inactive" },
      { channel: "facebook", status: "inactive" },
    ],
    proposals: [
      { id: "prop_neg_01", number: "NEG-2086", amount: 620000, status: "negotiating", proposerName: "Maria Silva", receivedAt: iso(7) },
      { id: "prop_neg_02", number: "NEG-2082", amount: 600000, status: "rejected", proposerName: "Pedro Mendes", receivedAt: iso(20) },
    ],
    inspections: [
      { id: "insp_01", kind: "periodic", status: "completed", completedAt: iso(34), reportPdfUrl: "https://example.com/report.pdf" },
    ],
    shareLinks: [
      {
        id: "lnk_01",
        token: "TKN1ABC123XY",
        showAddress: false,
        includeVideos: false,
        createdAt: iso(12),
        viewCount: 47,
        revoked: false,
        note: "Whatsapp Maria",
      },
      {
        id: "lnk_02",
        token: "TKN2XYZ987PP",
        showAddress: true,
        includeVideos: true,
        createdAt: iso(5),
        expiresAt: iso(-25),
        viewCount: 12,
        revoked: false,
        note: "Cliente Ricardo (visita confirmada)",
      },
    ],
    partnerships: [
      {
        id: "pt_01",
        identifierKind: "email",
        identifier: "rafaela@exemplo.com",
        partnerName: "Rafaela Andrade",
        ownerPercent: 70,
        partnerPercent: 30,
        status: "accepted",
        note: "Cliente do bairro dela",
        invitedAt: iso(20),
        respondedAt: iso(18),
        hasActiveProposal: true,
      },
      {
        id: "pt_02",
        identifierKind: "creci",
        identifier: "12345-SP",
        ownerPercent: 80,
        partnerPercent: 20,
        status: "invited",
        invitedAt: iso(3),
      },
    ],
    transactions: [
      {
        id: "trx_01",
        kind: "sale",
        status: "canceled",
        amount: 640000,
        partyName: "Vinicius Carvalho",
        brokerName: "Carlos Ferreira",
        startDate: iso(420),
        dealCode: "VEN-1908",
        contractCode: "CT-1408",
        note: "Cliente desistiu após laudo de inspeção residencial.",
      },
    ],
  },
  {
    id: "prop_02",
    internalCode: "IMV-0126",
    publicCode: "IMV-000126",
    title: "Apto 2qts com vista mar · Boa Viagem",
    type: "apartment",
    category: "residential",
    operation: "sale_rent",
    status: "active",
    salePrice: 1200000.00,
    rentPrice: 4800.00,
    saleCommissionPercent: 6,
    rentCommissionPercent: 100,
    condoFee: 950.00,
    iptuYearly: 3200.00,
    bedrooms: 2,
    suites: 1,
    bathrooms: 2,
    parkingSpots: 1,
    builtArea: 95,
    totalArea: 95,
    floor: 12,
    features: ["pool", "gym", "concierge_24h", "elevator", "balcony", "accepts_financing"],
    description: "Apartamento 12º andar, vista mar irrestrita. Prédio com lazer completo.",
    ownerId: "person_07",
    ownerName: "Imóveis Boa Vista Ltda",
    address: {
      zipcode: "51020000",
      street: "Av. Boa Viagem",
      number: "5500",
      neighborhood: "Boa Viagem",
      city: "Recife",
      state: "PE",
      hideExactAddress: true,
    },
    images: [
      { id: "img_02_01", url: pix(1, "cover"), isCover: true, order: 0 },
      { id: "img_02_02", url: pix(1, "gallery", 0), isCover: false, order: 1 },
      { id: "img_02_03", url: pix(1, "gallery", 1), isCover: false, order: 2 },
    ],
    shareCode: "PRP2BBDE02",
    publicSlug: "apto-2qts-vista-mar-boa-viagem-imv-0126",
    createdAt: iso(28),
    updatedAt: iso(1),
    publishedAt: iso(27),
  },
  {
    id: "prop_03",
    internalCode: "IMV-0127",
    publicCode: "IMV-000127",
    title: "Cobertura duplex 4 suítes · Vila Olímpia",
    type: "penthouse",
    category: "residential",
    operation: "sale",
    status: "active",
    salePrice: 4500000.00,
    saleCommissionPercent: 5,
    condoFee: 3500.00,
    iptuYearly: 12000.00,
    bedrooms: 4,
    suites: 4,
    bathrooms: 5,
    parkingSpots: 4,
    builtArea: 380,
    totalArea: 420,
    floor: 28,
    features: [
      "pool",
      "barbecue",
      "balcony",
      "gym",
      "concierge_24h",
      "elevator",
      "air_conditioning",
      "sauna",
    ],
    description: "Cobertura impecável, piscina privativa, vista 360º.",
    ownerId: "person_09",
    ownerName: "Ricardo Alves",
    address: {
      zipcode: "04543010",
      neighborhood: "Vila Olímpia",
      city: "São Paulo",
      state: "SP",
      hideExactAddress: true,
    },
    images: [
      { id: "img_03_01", url: pix(2, "cover"), isCover: true, order: 0 },
      { id: "img_03_02", url: pix(2, "gallery", 0), isCover: false, order: 1 },
      { id: "img_03_03", url: pix(2, "gallery", 1), isCover: false, order: 2 },
    ],
    shareCode: "PRP3CCDF03",
    publicSlug: "cobertura-duplex-vila-olimpia-imv-0127",
    createdAt: iso(60),
    updatedAt: iso(7),
    publishedAt: iso(55),
  },
  {
    id: "prop_04",
    internalCode: "IMV-0128",
    publicCode: "IMV-000128",
    title: "Apto 1qt mobiliado · Centro",
    type: "apartment",
    category: "residential",
    operation: "rent",
    status: "active",
    rentPrice: 2200.00,
    condoFee: 480.00,
    iptuYearly: 900.00,
    bedrooms: 1,
    bathrooms: 1,
    parkingSpots: 0,
    builtArea: 42,
    totalArea: 42,
    floor: 6,
    features: ["furnished", "elevator", "pet_friendly"],
    description: "Próximo ao metrô. Pronto pra entrar.",
    ownerId: "person_02",
    ownerName: "Pedro Mendes",
    address: {
      zipcode: "01310100",
      street: "Av. Paulista",
      number: "2000",
      neighborhood: "Bela Vista",
      city: "São Paulo",
      state: "SP",
    },
    images: [
      { id: "img_04_01", url: pix(3, "cover"), isCover: true, order: 0 },
      { id: "img_04_02", url: pix(3, "gallery", 0), isCover: false, order: 1 },
      { id: "img_04_03", url: pix(3, "gallery", 1), isCover: false, order: 2 },
    ],
    shareCode: "PRP4DDEG04",
    publicSlug: "apto-1qt-mobiliado-centro-imv-0128",
    isManaged: true,
    managementFeePercent: 8,
    createdAt: iso(15),
    updatedAt: iso(3),
    publishedAt: iso(14),
    transactions: [
      {
        id: "trx_04_01",
        kind: "rent",
        status: "active",
        amount: 2200,
        partyName: "Lucas Oliveira",
        brokerName: "Carlos Ferreira",
        startDate: iso(335),
        endDate: iso(-25),
        dealCode: "LOC-2099",
        contractId: "ct_02",
        contractCode: "CT-0312",
        note: "Contrato 12 meses, locatário renovando.",
      },
      {
        id: "trx_04_02",
        kind: "rent",
        status: "closed",
        amount: 1900,
        partyName: "Mariana Costa",
        brokerName: "Carlos Ferreira",
        startDate: iso(720),
        endDate: iso(340),
        dealCode: "LOC-1801",
        contractCode: "CT-0241",
        note: "Inquilina mudou-se ao fim do contrato.",
      },
    ],
  },
  {
    id: "prop_05",
    internalCode: "IMV-0129",
    publicCode: "IMV-000129",
    title: "Terreno 500m² · Sim",
    type: "land",
    category: "residential",
    operation: "sale",
    status: "active",
    salePrice: 180000.00,
    builtArea: 0,
    totalArea: 500,
    features: ["accepts_financing"],
    description: "Lote plano, documentação OK.",
    ownerId: "person_07",
    ownerName: "Imóveis Boa Vista Ltda",
    address: {
      zipcode: "44086100",
      neighborhood: "Sim",
      city: "Feira de Santana",
      state: "BA",
    },
    images: [
      { id: "img_05_01", url: pix(4, "cover"), isCover: true, order: 0 },
      { id: "img_05_02", url: pix(4, "gallery", 0), isCover: false, order: 1 },
    ],
    shareCode: "PRP5EEFH05",
    publicSlug: "terreno-500m2-sim-imv-0129",
    createdAt: iso(90),
    updatedAt: iso(20),
    publishedAt: iso(88),
  },
  {
    id: "prop_06",
    internalCode: "IMV-0130",
    publicCode: "IMV-000130",
    title: "Sala comercial 35m² · Faria Lima",
    type: "commercial_room",
    category: "commercial",
    operation: "rent",
    status: "active",
    rentPrice: 5800.00,
    condoFee: 1200.00,
    iptuYearly: 2400.00,
    bathrooms: 1,
    parkingSpots: 1,
    builtArea: 35,
    floor: 14,
    features: ["air_conditioning", "concierge_24h", "elevator"],
    description: "Edifício comercial AAA. Pronto pra ocupação.",
    ownerId: "person_10",
    ownerName: "Investe + Holdings S.A.",
    address: {
      zipcode: "04547001",
      street: "Av. Faria Lima",
      number: "3500",
      neighborhood: "Itaim Bibi",
      city: "São Paulo",
      state: "SP",
    },
    images: [
      { id: "img_06_01", url: pix(5, "cover"), isCover: true, order: 0 },
      { id: "img_06_02", url: pix(5, "gallery", 0), isCover: false, order: 1 },
    ],
    shareCode: "PRP6FFGI06",
    publicSlug: "sala-comercial-faria-lima-imv-0130",
    createdAt: iso(50),
    updatedAt: iso(5),
    publishedAt: iso(48),
  },
  // Imóvel RECEBIDO via co-broking: o corretor atual é o parceiro (share ativo).
  // Sem ownerId/ownerName — o backend mascara o proprietário para o parceiro.
  {
    id: "prop_07",
    internalCode: "IMV-0131",
    publicCode: "IMV-000131",
    title: "Cobertura duplex vista mar · Ondina, Salvador",
    type: "apartment",
    category: "residential",
    operation: "sale",
    status: "active",
    salePrice: 1250000.0,
    condoFee: 1800.0,
    iptuYearly: 4200.0,
    bedrooms: 4,
    suites: 2,
    bathrooms: 4,
    parkingSpots: 3,
    builtArea: 220,
    floor: 12,
    features: ["pool", "elevator", "concierge_24h", "barbecue"],
    description:
      "Cobertura duplex com vista permanente para o mar, piscina privativa e área gourmet. Imóvel trabalhado em parceria de co-corretagem.",
    address: {
      zipcode: "40170110",
      street: "Av. Oceânica",
      number: "2400",
      neighborhood: "Ondina",
      city: "Salvador",
      state: "BA",
    },
    images: [
      { id: "img_07_01", url: pix(6, "cover"), isCover: true, order: 0 },
      { id: "img_07_02", url: pix(6, "gallery", 0), isCover: false, order: 1 },
    ],
    shareCode: "PRP7GGHJ07",
    publicSlug: "cobertura-duplex-vista-mar-ondina-imv-0131",
    createdAt: iso(12),
    updatedAt: iso(2),
    publishedAt: iso(10),
    viewerRole: "partner",
    coBroking: { fromName: "Rafaela Costa", splitPercent: 40 },
  },
];

import { matchesOperation } from "../operation-filter";

export type ListParams = {
  type?: PropertyType;
  operation?: PropertyOperation;
  status?: PropertyStatus;
  city?: string;
  neighborhood?: string;
  q?: string;
  condominium?: boolean;
};

export async function listProperties(params?: ListParams): Promise<Property[]> {
  await randomDelay();
  const q = params?.q?.toLowerCase().trim();
  return store.filter((p) => {
    if (params?.type && p.type !== params.type) return false;
    if (!matchesOperation(p.operation, params?.operation)) return false;
    if (params?.status && p.status !== params.status) return false;
    if (params?.city && !p.address.city.toLowerCase().includes(params.city.toLowerCase()))
      return false;
    if (
      params?.neighborhood &&
      !p.address.neighborhood.toLowerCase().includes(params.neighborhood.toLowerCase())
    )
      return false;
    if (q) {
      const haystack = [
        p.title,
        p.internalCode,
        p.description,
        p.address.neighborhood,
        p.address.city,
        p.ownerName,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    return true;
  });
}

export async function getProperty(id: string): Promise<Property> {
  await randomDelay(200, 400);
  const found = store.find((p) => p.id === id);
  if (!found) throw new Error("PROPERTY_NOT_FOUND");
  return { ...found };
}

/** Métricas mock do link público — demo da tela "Compartilhar & Métricas". hub#577. */
export async function getShareAnalytics(id: string): Promise<ShareAnalytics> {
  await randomDelay(200, 500);
  const found = store.find((p) => p.id === id);
  const shareCode = found?.shareCode ?? "DEMOSHARE";
  const now = Date.now();
  const min = 60_000;
  const recent: ShareVisitRecent[] = [
    { occurredAt: new Date(now - 8 * min).toISOString(), deviceKind: "mobile", deviceName: "Celular Android", browser: "Chrome 120", os: "Android 13" },
    { occurredAt: new Date(now - 95 * min).toISOString(), deviceKind: "mobile", deviceName: "iPhone", browser: "Safari 17", os: "iOS 17.0" },
    { occurredAt: new Date(now - 5 * 60 * min).toISOString(), deviceKind: "desktop", deviceName: "Windows", browser: "Chrome 120", os: "Windows" },
    { occurredAt: new Date(now - 26 * 60 * min).toISOString(), deviceKind: "mobile", deviceName: "Celular Android", browser: "Chrome 119", os: "Android 12" },
  ];
  const day = (offset: number) => new Date(now - offset * 24 * 60 * min).toISOString().slice(0, 10);
  return {
    shareCode,
    publicUrl: `https://imobgo.app/public/p/${shareCode}`,
    totalClicks: 24,
    uniqueVisitors: 17,
    firstClickAt: new Date(now - 9 * 24 * 60 * min).toISOString(),
    lastClickAt: recent[0]!.occurredAt,
    timeline: [
      { date: day(4), clicks: 2 },
      { date: day(3), clicks: 3 },
      { date: day(2), clicks: 5 },
      { date: day(1), clicks: 11 },
      { date: day(0), clicks: 3 },
    ],
    byDeviceKind: { mobile: 18, desktop: 5, tablet: 1 },
    byBrowser: { Chrome: 16, Safari: 7, Other: 1 },
    byOs: { Android: 12, iOS: 7, Windows: 5 },
    recent,
  };
}

/** Página de acessos mock — devolve tudo numa página só (sem paginação real). hub#577. */
export async function getShareVisitsPage(
  id: string,
  _cursor?: string,
  _limit?: number,
): Promise<ShareVisitsPage> {
  const analytics = await getShareAnalytics(id);
  return { items: analytics.recent, nextCursor: null, hasMore: false, count: analytics.recent.length };
}

export async function getPropertyByCode(code: string): Promise<PublicProperty> {
  await randomDelay(200, 500);
  const target = code.toUpperCase();
  const found = store.find((p) => p.shareCode === target);
  if (!found) throw new Error("PROPERTY_NOT_FOUND");
  // Espelha o endpoint público real: projeta para o shape enxuto (sem
  // owner/comissão/parcerias/share links). Auditoria P2-public-property-fe-type.
  return toPublicProperty(found);
}

function slugify(s: string) {
  return s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function nextCode(): string {
  const max = store.reduce((acc, p) => {
    const m = p.internalCode.match(/IMV-(\d+)/);
    const n = m ? Number.parseInt(m[1]!, 10) : 0;
    return n > acc ? n : acc;
  }, 0);
  return `IMV-${String(max + 1).padStart(4, "0")}`;
}

export async function createProperty(
  input: Omit<
    Property,
    "id" | "internalCode" | "publicSlug" | "shareCode" | "createdAt" | "updatedAt" | "images"
  > & { images?: PropertyImage[] },
): Promise<Property> {
  await randomDelay();
  const id = `prop_${(store.length + 1).toString().padStart(2, "0")}_${Math.random()
    .toString(36)
    .slice(2, 6)}`;
  const code = nextCode();
  const slug = slugify(input.title) + "-" + code.toLowerCase();
  const shareCode = generateShareCode();
  const ts = new Date().toISOString();
  const created: Property = {
    ...input,
    id,
    internalCode: code,
    publicCode: `IMV-${(130 + store.length + 1).toString().padStart(6, "0")}`,
    publicSlug: slug,
    shareCode,
    images: input.images ?? [],
    videos: input.videos ?? [],
    createdAt: ts,
    updatedAt: ts,
    publishedAt: input.status === "active" ? ts : undefined,
  };
  store = [created, ...store];
  return created;
}

export async function updateProperty(
  id: string,
  input: Partial<
    Omit<Property, "id" | "internalCode" | "publicSlug" | "shareCode" | "createdAt" | "updatedAt">
  >,
): Promise<Property> {
  await randomDelay();
  const idx = store.findIndex((p) => p.id === id);
  if (idx === -1) throw new Error("PROPERTY_NOT_FOUND");
  const current = store[idx]!;
  const updated: Property = { ...current, ...input, id, updatedAt: new Date().toISOString() };
  store = store.map((p) => (p.id === id ? updated : p));
  return updated;
}

export async function deleteProperty(id: string): Promise<{ ok: true }> {
  await randomDelay();
  store = store.filter((p) => p.id !== id);
  return { ok: true };
}

/** Mock do upload presigned: cria a foto direto no store (preview via objectURL). */
export async function uploadPropertyImage(
  propertyId: string,
  file: Blob,
  position: number,
): Promise<PropertyImage> {
  await randomDelay();
  const image: PropertyImage = {
    id: `img_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    url: URL.createObjectURL(file),
    isCover: position === 0,
    order: position,
  };
  store = store.map((p) =>
    p.id === propertyId ? { ...p, images: [...p.images, image] } : p,
  );
  return image;
}

export async function deletePropertyImage(propertyId: string, imageId: string): Promise<void> {
  await randomDelay();
  store = store.map((p) => {
    if (p.id !== propertyId) return p;
    const images = p.images
      .filter((i) => i.id !== imageId)
      .sort((a, b) => a.order - b.order)
      .map((img, idx) => ({ ...img, order: idx, isCover: idx === 0 }));
    return { ...p, images };
  });
}

export async function reorderPropertyImages(
  propertyId: string,
  imageIds: string[],
): Promise<void> {
  await randomDelay();
  store = store.map((p) => {
    if (p.id !== propertyId) return p;
    const images = imageIds
      .map((id, idx) => {
        const img = p.images.find((i) => i.id === id)!;
        return { ...img, order: idx, isCover: idx === 0 };
      })
      .filter(Boolean);
    return { ...p, images };
  });
}

export async function changePropertyStatus(
  id: string,
  status: PropertyStatus,
): Promise<Property> {
  return updateProperty(id, { status });
}

// ---------- Vídeos curtos via Vimeo (hub#636) ----------

/**
 * Momento (epoch ms) em que cada vídeo mockado passa de `processing` para `ready`.
 * Simula o transcode do Vimeo: `getPropertyVideoStatus` vira "ready" após ~4 s,
 * permitindo que a tela de detalhe exercite o polling de status como no real.
 */
const mockVideoReadyAt = new Map<string, number>();

/** Embed público de amostra reutilizado quando um vídeo mock fica "ready". */
const MOCK_READY_EMBED = "https://player.vimeo.com/video/76979871";

/**
 * Mock do ticket de upload: cria o vídeo em `processing` (como se os bytes já
 * tivessem subido via tus) e agenda a transição para `ready`. O real faz
 * POST upload-ticket → tus-upload; aqui não há binário para subir de fato.
 */
export async function createVideoUploadTicket(
  propertyId: string,
  file: File,
  caption?: string,
): Promise<PropertyVideo> {
  await randomDelay(300, 700);
  const target = store.find((p) => p.id === propertyId);
  if (!target) throw new Error("PROPERTY_NOT_FOUND");
  const existing = target.videos ?? [];
  if (existing.length >= MAX_PROPERTY_VIDEOS) throw new Error("PROPERTY_VIDEO_LIMIT");
  const id = `vid_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
  const video: PropertyVideo = {
    id,
    vimeoId: `mock-${Math.random().toString(36).slice(2, 10)}`,
    status: "processing",
    order: existing.length,
    caption: caption?.trim() || undefined,
  };
  void file;
  store = store.map((p) =>
    p.id === propertyId ? { ...p, videos: [...existing, video] } : p,
  );
  mockVideoReadyAt.set(id, Date.now() + 4000);
  return video;
}

/** Mock do polling de status: promove a `ready` (com embedUrl) após o prazo simulado. */
export async function getPropertyVideoStatus(
  propertyId: string,
  videoId: string,
): Promise<PropertyVideo> {
  await randomDelay(150, 350);
  const target = store.find((p) => p.id === propertyId);
  const current = (target?.videos ?? []).find((v) => v.id === videoId);
  if (!current) throw new Error("PROPERTY_VIDEO_NOT_FOUND");
  const readyAt = mockVideoReadyAt.get(videoId);
  if (current.status !== "ready" && readyAt != null && Date.now() >= readyAt) {
    const ready: PropertyVideo = {
      ...current,
      status: "ready",
      embedUrl: MOCK_READY_EMBED,
      durationSeconds: current.durationSeconds ?? 30,
    };
    store = store.map((p) =>
      p.id === propertyId
        ? { ...p, videos: (p.videos ?? []).map((v) => (v.id === videoId ? ready : v)) }
        : p,
    );
    mockVideoReadyAt.delete(videoId);
    return ready;
  }
  return current;
}

export async function deletePropertyVideo(propertyId: string, videoId: string): Promise<void> {
  await randomDelay();
  store = store.map((p) => {
    if (p.id !== propertyId) return p;
    const videos = (p.videos ?? [])
      .filter((v) => v.id !== videoId)
      .sort((a, b) => a.order - b.order)
      .map((v, idx) => ({ ...v, order: idx }));
    return { ...p, videos };
  });
}

export async function reorderPropertyVideos(
  propertyId: string,
  videoIds: string[],
): Promise<void> {
  await randomDelay();
  store = store.map((p) => {
    if (p.id !== propertyId) return p;
    const videos = videoIds
      .map((id, idx) => {
        const vid = (p.videos ?? []).find((v) => v.id === id)!;
        return { ...vid, order: idx };
      })
      .filter(Boolean);
    return { ...p, videos };
  });
}

// ---------- Foto de preview do WhatsApp (hub#577) ----------

/**
 * Mock do 1º passo presigned: no real devolve o `mediaAssetId`; aqui devolvemos o
 * objectURL do arquivo escolhido, que o `setPreviewImage` usa como URL da preview.
 */
export async function uploadPreviewImageAsset(file: Blob): Promise<string> {
  await randomDelay();
  return URL.createObjectURL(file);
}

export async function setPreviewImage(id: string, mediaAssetId: string): Promise<Property> {
  // No mock o mediaAssetId É o objectURL (ver uploadPreviewImageAsset).
  return updateProperty(id, { previewImageUrl: mediaAssetId });
}

export async function clearPreviewImage(id: string): Promise<Property> {
  return updateProperty(id, { previewImageUrl: null });
}

// ---------- Share-links ----------

export async function createShareLink(
  propertyId: string,
  input: {
    showAddress: boolean;
    includeVideos?: boolean;
    note?: string;
    expiresInDays?: number;
    maxViews?: number;
    hideBrokerIdentity?: boolean;
    requesterName?: string | null;
    requesterEmail?: string | null;
    requesterPhone?: string | null;
  },
): Promise<PropertyShareLink> {
  await randomDelay(200, 400);
  const link: PropertyShareLink = {
    id: `lnk_${Math.random().toString(36).slice(2, 8)}`,
    token: generateShareCode().padEnd(12, "Z"),
    showAddress: input.showAddress,
    includeVideos: input.includeVideos ?? false,
    note: input.note,
    maxViews: input.maxViews,
    expiresAt: input.expiresInDays
      ? new Date(Date.now() + input.expiresInDays * 86_400_000).toISOString()
      : undefined,
    viewCount: 0,
    revoked: false,
    hideBrokerIdentity: input.hideBrokerIdentity ?? false,
    // Fora do modo sem identidade o servidor descarta os dados do solicitante — o mock
    // espelha isso para a tela se comportar igual em mock e real.
    requesterName: input.hideBrokerIdentity ? (input.requesterName ?? null) : null,
    createdAt: new Date().toISOString(),
  };
  const target = store.find((p) => p.id === propertyId);
  if (!target) throw new Error("PROPERTY_NOT_FOUND");
  const next: Property = {
    ...target,
    shareLinks: [...(target.shareLinks ?? []), link],
    updatedAt: new Date().toISOString(),
  };
  store = store.map((p) => (p.id === propertyId ? next : p));
  return link;
}

export async function revokeShareLink(propertyId: string, linkId: string): Promise<Property> {
  await randomDelay(150, 300);
  const target = store.find((p) => p.id === propertyId);
  if (!target) throw new Error("PROPERTY_NOT_FOUND");
  const updated: Property = {
    ...target,
    shareLinks: (target.shareLinks ?? []).map((l) =>
      l.id === linkId ? { ...l, revoked: true } : l,
    ),
    updatedAt: new Date().toISOString(),
  };
  store = store.map((p) => (p.id === propertyId ? updated : p));
  return updated;
}

/** Excluir tira da lista; revogar só marca. */
export async function deleteShareLink(propertyId: string, linkId: string): Promise<Property> {
  await randomDelay(150, 300);
  const target = store.find((p) => p.id === propertyId);
  if (!target) throw new Error("PROPERTY_NOT_FOUND");
  const updated: Property = {
    ...target,
    shareLinks: (target.shareLinks ?? []).filter((l) => l.id !== linkId),
    updatedAt: new Date().toISOString(),
  };
  store = store.map((p) => (p.id === propertyId ? updated : p));
  return updated;
}

// ---------- Partnerships ----------

/** Convite ao corretor parceiro EXTERNO na proposta (link público + e-mail). hub#412. */
export interface ExternalPartnerInvite {
  id: string;
  token: string;
  url: string;
  status: "pending" | "submitted" | "expired" | "cancelled";
  name?: string;
  email?: string;
  phone?: string;
  isWhatsApp: boolean;
  createdAt: string;
  expiresAt: string;
}

/** Dados de contato do parceiro + escolha de gerar só o link ou também enviar e-mail. */
export interface GenerateExternalPartnerInviteInput {
  name?: string;
  email?: string;
  phone?: string;
  isWhatsApp?: boolean;
  sendEmail?: boolean;
}

// Store em memória só para o mock conseguir LISTAR o que foi gerado (acompanhamento).
const externalPartnerInvitesStore: Array<ExternalPartnerInvite & { propertyId: string }> = [];

export async function generateExternalPartnerInvite(
  propertyId: string,
  input: GenerateExternalPartnerInviteInput = {},
): Promise<ExternalPartnerInvite> {
  await randomDelay(200, 500);
  if (!store.find((p) => p.id === propertyId)) throw new Error("PROPERTY_NOT_FOUND");
  const token = `${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
  const phone = input.phone?.trim() || undefined;
  const invite: ExternalPartnerInvite = {
    id: `epi_${Math.random().toString(36).slice(2, 8)}`,
    token,
    url: publicProposalPartnerUrl(token),
    status: "pending",
    name: input.name?.trim() || undefined,
    email: input.email?.trim() || undefined,
    phone,
    isWhatsApp: Boolean(phone && input.isWhatsApp),
    createdAt: new Date().toISOString(),
    expiresAt: new Date(Date.now() + 7 * 864e5).toISOString(),
  };
  externalPartnerInvitesStore.unshift({ ...invite, propertyId });
  return invite;
}

export async function listExternalPartnerInvites(propertyId?: string): Promise<ExternalPartnerInvite[]> {
  await randomDelay(150, 350);
  return externalPartnerInvitesStore
    .filter((i) => !propertyId || i.propertyId === propertyId)
    .map(({ propertyId: _omit, ...rest }) => rest);
}

export async function cancelExternalPartnerInvite(inviteId: string): Promise<{ ok: true }> {
  await randomDelay(150, 300);
  const found = externalPartnerInvitesStore.find((i) => i.id === inviteId);
  if (found) found.status = "cancelled";
  return { ok: true };
}

/** Indicação de cliente em co-corretagem com parceiro usuário da plataforma. hub#412. */
export interface CoBrokingClient {
  id: string;
  propertyShareId: string;
  propertyId: string;
  ownerBrokerId: string;
  partnerBrokerId: string;
  clientName: string;
  clientEmail?: string;
  clientCellphone?: string;
  clientRole?: string;
  status: "active" | "revoked";
  importedClientId?: string;
  createdAt: string;
}

export async function indicateCoBrokingClient(
  shareId: string,
  input: { personId: string; role?: string },
): Promise<CoBrokingClient> {
  await randomDelay(200, 500);
  return {
    id: `cbc_${Math.random().toString(36).slice(2, 8)}`,
    propertyShareId: shareId,
    propertyId: `prop_${Math.random().toString(36).slice(2, 6)}`,
    ownerBrokerId: "broker_owner",
    partnerBrokerId: "broker_self",
    clientName: "Cliente Indicado",
    clientRole: input.role,
    status: "active",
    createdAt: new Date().toISOString(),
  };
}

export async function listCoBrokingClients(_shareId: string): Promise<CoBrokingClient[]> {
  await randomDelay(150, 300);
  return [];
}

export async function revokeCoBrokingClient(
  _shareId: string,
  _linkId: string,
): Promise<{ ok: true }> {
  await randomDelay(150, 300);
  return { ok: true };
}

// ADR-0042: importCoBrokingClient removido — a importação do cliente para a base da
// captação saiu (endpoint responde 410 Gone). B usa o próprio Person na proposta.

export async function createPartnership(
  propertyId: string,
  input: {
    identifierKind: PartnerIdentifierKind;
    identifier: string;
    partnerPercent: number;
    note?: string;
  },
): Promise<PropertyPartnership> {
  await randomDelay(200, 500);
  const partnership: PropertyPartnership = {
    id: `pt_${Math.random().toString(36).slice(2, 8)}`,
    identifierKind: input.identifierKind,
    identifier: input.identifier,
    partnerPercent: input.partnerPercent,
    ownerPercent: 100 - input.partnerPercent,
    status: "invited",
    note: input.note,
    invitedAt: new Date().toISOString(),
  };
  const target = store.find((p) => p.id === propertyId);
  if (!target) throw new Error("PROPERTY_NOT_FOUND");
  const next: Property = {
    ...target,
    partnerships: [...(target.partnerships ?? []), partnership],
    updatedAt: new Date().toISOString(),
  };
  store = store.map((p) => (p.id === propertyId ? next : p));
  return partnership;
}

export async function revokePartnership(
  propertyId: string,
  partnershipId: string,
): Promise<Property> {
  await randomDelay(150, 300);
  const target = store.find((p) => p.id === propertyId);
  if (!target) throw new Error("PROPERTY_NOT_FOUND");
  const updated: Property = {
    ...target,
    partnerships: (target.partnerships ?? []).map((pt) =>
      pt.id === partnershipId
        ? { ...pt, status: "revoked" as PartnershipStatus, revokedAt: new Date().toISOString() }
        : pt,
    ),
    updatedAt: new Date().toISOString(),
  };
  store = store.map((p) => (p.id === propertyId ? updated : p));
  return updated;
}

/** Excluir tira da lista, inclusive a parceria ativa; revogar só marca. */
export async function deletePartnership(
  propertyId: string,
  partnershipId: string,
): Promise<Property> {
  await randomDelay(150, 300);
  const target = store.find((p) => p.id === propertyId);
  if (!target) throw new Error("PROPERTY_NOT_FOUND");
  const updated: Property = {
    ...target,
    partnerships: (target.partnerships ?? []).filter((pt) => pt.id !== partnershipId),
    updatedAt: new Date().toISOString(),
  };
  store = store.map((p) => (p.id === propertyId ? updated : p));
  return updated;
}

/** Mock da busca global por código público IMV-: resolve só imóveis do store (próprio corretor). */
export async function lookupProperty(code: string): Promise<PropertyLookupResult> {
  await randomDelay(150, 350);
  const normalized = code?.trim().toUpperCase();
  if (!normalized) return { found: false };
  const hit = store.find((p) => p.publicCode?.toUpperCase() === normalized);
  return hit
    ? { found: true, propertyId: hit.id, publicCode: hit.publicCode, title: hit.title, status: hit.status }
    : { found: false };
}

/** Mock do lookup: acha um corretor fictício se o CRECI/código não estiver "vazio". */
export async function lookupBroker(params: {
  creci?: string;
  uf?: string;
  code?: string;
}): Promise<BrokerLookupResult> {
  await randomDelay(150, 350);
  const code = params.code?.trim().toUpperCase();
  const creci = params.creci?.trim();
  if (code) {
    return /^IMB-[0-9][0-9A-Z]{0,11}$/i.test(code)
      ? { found: true, brokerId: `bk_${code}`, publicCode: code, name: "Marina Co-corretora" }
      : { found: false };
  }
  if (creci && params.uf) {
    return { found: true, brokerId: `bk_${creci}_${params.uf}`, publicCode: "IMB-00731", name: "Marina Co-corretora" };
  }
  return { found: false };
}

function setPartnershipStatus(
  propertyId: string,
  partnershipId: string,
  status: PartnershipStatus,
): Property {
  const target = store.find((p) => p.id === propertyId);
  if (!target) throw new Error("PROPERTY_NOT_FOUND");
  const updated: Property = {
    ...target,
    partnerships: (target.partnerships ?? []).map((pt) =>
      pt.id === partnershipId
        ? { ...pt, status, respondedAt: new Date().toISOString() }
        : pt,
    ),
    updatedAt: new Date().toISOString(),
  };
  store = store.map((p) => (p.id === propertyId ? updated : p));
  return updated;
}

export async function acceptPartnership(propertyId: string, partnershipId: string): Promise<Property> {
  await randomDelay(150, 350);
  return setPartnershipStatus(propertyId, partnershipId, "accepted");
}

export async function declinePartnership(propertyId: string, partnershipId: string): Promise<Property> {
  await randomDelay(150, 350);
  return setPartnershipStatus(propertyId, partnershipId, "declined");
}

// ---------- Convites de co-corretagem recebidos (caixa do convidado) ----------
let receivedPartnerships: ReceivedPartnership[] = [
  {
    id: "rp_1",
    propertyId: "ext_prop_1",
    propertyTitle: "Apartamento 2qts · Itaigara, Salvador",
    propertyCode: "IMV-20481",
    ownerBrokerName: "Rafaela Andrade",
    ownerPercent: 60,
    partnerPercent: 40,
    status: "invited",
    note: "Cliente seu pode fechar essa? Divido 40%.",
    invitedAt: iso(1),
  },
  {
    id: "rp_2",
    propertyId: "ext_prop_2",
    propertyTitle: "Casa em condomínio · Patamares, Salvador",
    propertyCode: "IMV-19920",
    ownerBrokerName: "Tiago Mendes",
    ownerPercent: 50,
    partnerPercent: 50,
    status: "invited",
    invitedAt: iso(4),
  },
  {
    id: "rp_3",
    propertyId: "ext_prop_3",
    propertyTitle: "Cobertura · Pituba, Salvador",
    propertyCode: "IMV-18733",
    ownerBrokerName: "Rafaela Andrade",
    ownerPercent: 70,
    partnerPercent: 30,
    status: "accepted",
    invitedAt: iso(10),
  },
];

export async function listReceivedPartnerships(status?: string): Promise<ReceivedPartnership[]> {
  await randomDelay();
  return status ? receivedPartnerships.filter((r) => r.status === status) : receivedPartnerships;
}

export async function acceptReceivedPartnership(partnershipId: string): Promise<void> {
  await randomDelay(150, 350);
  receivedPartnerships = receivedPartnerships.map((r) =>
    r.id === partnershipId ? { ...r, status: "accepted" } : r,
  );
}

export async function declineReceivedPartnership(partnershipId: string): Promise<void> {
  await randomDelay(150, 350);
  receivedPartnerships = receivedPartnerships.map((r) =>
    r.id === partnershipId ? { ...r, status: "declined" } : r,
  );
}

// ---------- Autorizações de acesso ao imóvel ----------

/**
 * Persistência em localStorage pra simular o backend real:
 * links abertos em outra aba/dispositivo precisam achar o token.
 * Cada autorização é salva com snapshot do imóvel (endereço, título,
 * código) — assim o link público funciona mesmo sem auth.
 */
const STORAGE_KEY = "imobgo.mock.accessAuthorizations";

interface StoredAuth {
  property: Pick<Property, "id" | "title" | "internalCode" | "address" | "ownerName"> & {
    coverImageUrl?: string;
  };
  authorization: PropertyAccessAuthorization;
}

function readPersistedAuths(): Record<string, StoredAuth> {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Record<string, StoredAuth>) : {};
  } catch {
    return {};
  }
}

function writePersistedAuths(map: Record<string, StoredAuth>) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore (quota / private mode) */
  }
}

function persistAuth(property: Property, auth: PropertyAccessAuthorization) {
  const map = readPersistedAuths();
  map[auth.token] = {
    property: {
      id: property.id,
      title: property.title,
      internalCode: property.internalCode,
      address: property.address,
      ownerName: property.ownerName,
      coverImageUrl: property.images.find((i) => i.isCover)?.url ?? property.images[0]?.url,
    },
    authorization: auth,
  };
  writePersistedAuths(map);
}

function patchPersistedAuth(token: string, patch: Partial<PropertyAccessAuthorization>) {
  const map = readPersistedAuths();
  const existing = map[token];
  if (!existing) return;
  map[token] = {
    ...existing,
    authorization: { ...existing.authorization, ...patch },
  };
  writePersistedAuths(map);
}

function ensureOwner(target: Property): void {
  if (target.viewerRole === "partner") {
    throw new Error("ACCESS_AUTHORIZATION_FORBIDDEN_FOR_PARTNER");
  }
}

function genAuthToken(): string {
  const rand = () => Math.random().toString(36).slice(2, 10);
  return `${rand()}${rand()}`;
}

function genVisitorId(): string {
  return `viz_${Math.random().toString(36).slice(2, 8)}`;
}

export async function createAccessAuthorization(
  propertyId: string,
  input: {
    scheduledAt: string;
    durationMinutes?: number;
    visitors: { nome: string; documento?: string; telefone?: string }[];
    ownerPhone: string;
    note?: string;
    requestedByName?: string;
  },
): Promise<PropertyAccessAuthorization> {
  await randomDelay(250, 500);
  const target = store.find((p) => p.id === propertyId);
  if (!target) throw new Error("PROPERTY_NOT_FOUND");
  ensureOwner(target);
  const auth: PropertyAccessAuthorization = {
    id: `acc_${Math.random().toString(36).slice(2, 8)}`,
    token: genAuthToken(),
    scheduledAt: input.scheduledAt,
    durationMinutes: input.durationMinutes,
    visitors: input.visitors.map((v) => ({ id: genVisitorId(), ...v })),
    ownerPhone: input.ownerPhone,
    note: input.note,
    status: "requested",
    requestedByName: input.requestedByName ?? "Corretor",
    createdAt: new Date().toISOString(),
  };
  const updated: Property = {
    ...target,
    accessAuthorizations: [...(target.accessAuthorizations ?? []), auth],
    updatedAt: new Date().toISOString(),
  };
  store = store.map((p) => (p.id === propertyId ? updated : p));
  persistAuth(updated, auth);
  return auth;
}

export async function cancelAccessAuthorization(
  propertyId: string,
  authId: string,
): Promise<Property> {
  await randomDelay(150, 300);
  const target = store.find((p) => p.id === propertyId);
  if (!target) throw new Error("PROPERTY_NOT_FOUND");
  ensureOwner(target);
  const updated: Property = {
    ...target,
    accessAuthorizations: (target.accessAuthorizations ?? []).map((a) =>
      a.id === authId && (a.status === "requested" || a.status === "expired")
        ? { ...a, status: "canceled" as AccessAuthorizationStatus }
        : a,
    ),
    updatedAt: new Date().toISOString(),
  };
  store = store.map((p) => (p.id === propertyId ? updated : p));
  // Atualiza status no localStorage também (canceled)
  const canceled = updated.accessAuthorizations?.find((a) => a.id === authId);
  if (canceled) patchPersistedAuth(canceled.token, { status: canceled.status });
  return updated;
}

/** Busca uma autorização pelo token público (sem auth). */
export async function getAccessAuthorizationByToken(token: string): Promise<{
  property: Pick<Property, "id" | "title" | "internalCode" | "address" | "ownerName"> & {
    coverImageUrl?: string;
  };
  authorization: PropertyAccessAuthorization;
} | null> {
  await randomDelay(120, 260);
  // 1) tenta no store em memória (mesma aba)
  for (const p of store) {
    const auth = (p.accessAuthorizations ?? []).find((a) => a.token === token);
    if (auth) {
      return {
        property: {
          id: p.id,
          title: p.title,
          internalCode: p.internalCode,
          address: p.address,
          ownerName: p.ownerName,
          coverImageUrl: p.images.find((i) => i.isCover)?.url ?? p.images[0]?.url,
        },
        authorization: auth,
      };
    }
  }
  // 2) fallback: localStorage (aba diferente / refresh)
  const persisted = readPersistedAuths()[token];
  return persisted ?? null;
}

/** Endpoint público: proprietário responde via link sem auth. */
export async function respondAccessAuthorization(
  token: string,
  response: "authorized" | "denied",
  note?: string,
): Promise<PropertyAccessAuthorization> {
  await randomDelay(200, 400);
  // 1) atualiza no store se existir nessa aba
  for (const p of store) {
    const idx = (p.accessAuthorizations ?? []).findIndex((a) => a.token === token);
    if (idx >= 0) {
      const current = p.accessAuthorizations![idx]!;
      if (current.status !== "requested") {
        throw new Error("ACCESS_AUTHORIZATION_ALREADY_RESPONDED");
      }
      const updatedAuth: PropertyAccessAuthorization = {
        ...current,
        status: response,
        ownerRespondedAt: new Date().toISOString(),
        ownerResponseNote: note,
      };
      const updatedProperty: Property = {
        ...p,
        accessAuthorizations: p.accessAuthorizations!.map((a, i) => (i === idx ? updatedAuth : a)),
        updatedAt: new Date().toISOString(),
      };
      store = store.map((it) => (it.id === p.id ? updatedProperty : it));
      patchPersistedAuth(token, {
        status: response,
        ownerRespondedAt: updatedAuth.ownerRespondedAt,
        ownerResponseNote: note,
      });
      return updatedAuth;
    }
  }
  // 2) fallback: a aba do proprietário não tem o store, usa só localStorage
  const persisted = readPersistedAuths()[token];
  if (persisted) {
    if (persisted.authorization.status !== "requested") {
      throw new Error("ACCESS_AUTHORIZATION_ALREADY_RESPONDED");
    }
    const updatedAuth: PropertyAccessAuthorization = {
      ...persisted.authorization,
      status: response,
      ownerRespondedAt: new Date().toISOString(),
      ownerResponseNote: note,
    };
    patchPersistedAuth(token, {
      status: response,
      ownerRespondedAt: updatedAuth.ownerRespondedAt,
      ownerResponseNote: note,
    });
    return updatedAuth;
  }
  throw new Error("ACCESS_AUTHORIZATION_NOT_FOUND");
}
